import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Expiry validation error details.
 */
export interface ExpiryValidationError {
  reason: 'format' | 'expired' | 'invalidMonth';
  message: string;
}

/**
 * Validate credit card expiry date with end-of-month awareness.
 * Cards are valid through the LAST day of their expiry month.
 *
 * @param value - Expiry date in MM/YY format
 * @returns Validation error object or null if valid
 *
 * @example
 * // On January 15, 2026:
 * validateExpiry('01/26') // null (valid - expires Jan 31, 2026)
 * validateExpiry('12/25') // { reason: 'expired', ... } (expired Dec 31, 2025)
 * validateExpiry('13/26') // { reason: 'invalidMonth', ... }
 * validateExpiry('1226')  // { reason: 'format', ... }
 */
export function validateExpiry(value: string): ExpiryValidationError | null {
  if (!value || value.trim() === '') {
    return null; // Let required validator handle empty
  }

  // Check format MM/YY
  const match = value.match(/^(0[1-9]|1[0-2])\/(\d{2})$/);
  if (!match) {
    return {
      reason: 'format',
      message: 'Invalid format. Use MM/YY.',
    };
  }

  const month = parseInt(match[1], 10);
  const year = parseInt(match[2], 10) + 2000; // Convert YY to YYYY

  // Validate month range (should be caught by regex, but double-check)
  if (month < 1 || month > 12) {
    return {
      reason: 'invalidMonth',
      message: 'Invalid month. Must be 01-12.',
    };
  }

  // Card is valid through the END of the expiry month
  // Create date for last day of expiry month at end of day
  // Month in Date constructor is 0-indexed, so month (1-12) gives us the next month
  // Day 0 gives us the last day of the previous month (which is our target month)
  const lastDayOfExpiryMonth = new Date(year, month, 0, 23, 59, 59, 999);

  const now = new Date();

  if (lastDayOfExpiryMonth < now) {
    return {
      reason: 'expired',
      message: 'Card has expired.',
    };
  }

  return null;
}

/**
 * Angular ValidatorFn wrapper for validateExpiry.
 * For use with Reactive Forms.
 */
export function expiryValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const error = validateExpiry(control.value);
    return error ? { expiry: error } : null;
  };
}
